import { api, traversalApi, transformationsApi } from '@ardoq/api';
import { getWildcardTraversalPayload } from 'appLayout/ardoqStudio/utils';
import {
  APIScopeData,
  ArdoqId,
  FutureStateApprovalSummary,
  FutureStateWebsocketEvent,
  Initiative,
  InitiativeOwner,
  InitiativePagePermissions,
  InitiativePageResponse,
  InitiativeUpdateResponse,
  InitiativeUpdatedEvent,
  InitiativeSummary,
  ResourceType,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { scopeDataOperations } from '@ardoq/scope-data';
import {
  isArdoqError,
  isTruthy,
  Result,
  toArdoqError,
} from '@ardoq/common-helpers';
import { reducedStream, streamReducer } from '@ardoq/rxbeach';
import { filterResourceType, websocket$ } from 'sync/websocket$';
import { websocketReducer } from 'streams/crud/reducers';
import { location$, filterRouteActive } from 'router/declarative';
import {
  distinctUntilChanged,
  filter,
  from,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { isEqual } from 'lodash';
import { deleteFutureState$ } from './deleteFutureState$';
import { updateInitiative$ } from './updateInitiative$';
import { updateStage$ } from './updateStage$';
import { dispatchDetectedSetupStatus } from '../setupStatusDetection';
import { isFutureStateApproved } from '../futureStatePage/operations';
import type { TransformationsAccess } from '../writeInitiativeAccess';
import {
  addFutureStatePreview,
  mergeFutureStatePreview,
  removeFutureStatePreview,
} from '../futureStatePreviews';

type InitiativePageAccess = {
  [K in keyof InitiativePagePermissions]: TransformationsAccess;
};

const UNKNOWN_PERMISSIONS: InitiativePageAccess = {
  writeInitiative: 'unknown',
  linkObjectives: 'unknown',
  editOwners: 'unknown',
};

export type InitiativePage$State =
  | { isLoading: true }
  | {
      isLoading: false;
      initiativeResult: Result<Initiative>;
      owners: InitiativeOwner[];
      scopeData: APIScopeData | null;
      enhancedScopeData: EnhancedScopeData | null;
      initiativeScopeData: EnhancedScopeData | null;
      permissions: InitiativePageAccess;
    };

export const activeInitiativeId$ = location$.pipe(
  filterRouteActive('/transformations/initiatives/:initiativeId'),
  map(location => location.params.initiativeId),
  distinctUntilChanged()
);

const currentInitiative$ = activeInitiativeId$.pipe(
  switchMap(transformationsApi.fetchInitiative),
  tap(response => {
    api.logErrorIfNeeded(response);
    dispatchDetectedSetupStatus(response);
  })
);

const defaultState: InitiativePage$State = { isLoading: true };

export const toApprovalSummary = (
  initiativeResult: Result<Initiative>
): FutureStateApprovalSummary | null => {
  if (isArdoqError(initiativeResult)) return null;
  const approved = initiativeResult.futureStates.find(isFutureStateApproved);
  if (!approved?.approval) return null;
  return { ...approved.approval, futureStateId: approved._id };
};

type ToLoadedStateArgs = {
  initiativeResult: Result<Initiative>;
  owners: InitiativeOwner[];
  scopeData: APIScopeData | null;
  initiativeScopeData: EnhancedScopeData | null;
  permissions: InitiativePageAccess;
};

const toLoadedState = ({
  initiativeResult,
  owners,
  scopeData,
  initiativeScopeData,
  permissions,
}: ToLoadedStateArgs): InitiativePage$State => ({
  isLoading: false,
  initiativeResult,
  owners,
  scopeData,
  enhancedScopeData: scopeData ? scopeDataOperations.enhance(scopeData) : null,
  initiativeScopeData,
  permissions,
});

const mergeScopeData = (
  current: APIScopeData | null,
  incoming: APIScopeData
): APIScopeData => {
  if (!current) return incoming;
  return {
    ...scopeDataOperations.merge(
      { ...scopeDataOperations.getEmpty(), ...incoming },
      current
    ),
    dateRangeFieldMap: incoming.dateRangeFieldMap ?? current.dateRangeFieldMap,
  };
};

const rebuildInitiative = (
  state: InitiativePage$State & { isLoading: false },
  current: Initiative,
  { initiative, scopeData }: InitiativeUpdateResponse
): InitiativePage$State =>
  toLoadedState({
    initiativeResult: { ...initiative, futureStates: current.futureStates },
    owners: initiative.owners ?? [],
    scopeData,
    initiativeScopeData: state.initiativeScopeData,
    permissions: state.permissions,
  });

const setCurrentInitiative = (
  _state: InitiativePage$State,
  result: Result<InitiativePageResponse>
): InitiativePage$State => {
  if (isArdoqError(result)) {
    return toLoadedState({
      initiativeResult: result,
      owners: [],
      scopeData: null,
      initiativeScopeData: null,
      permissions: UNKNOWN_PERMISSIONS,
    });
  }
  return toLoadedState({
    initiativeResult: result.initiative,
    owners: result.initiative.owners ?? [],
    scopeData: result.scopeData,
    initiativeScopeData: null,
    permissions: result.permissions,
  });
};

const updateLoadedInitiative = (
  state: InitiativePage$State,
  update: (initiative: Initiative) => Initiative,
  scopeData: APIScopeData | null
): InitiativePage$State => {
  if (state.isLoading || isArdoqError(state.initiativeResult)) return state;
  const initiative = update(state.initiativeResult);
  if (initiative === state.initiativeResult) return state;
  return toLoadedState({
    initiativeResult: initiative,
    owners: state.owners,
    scopeData: scopeData
      ? mergeScopeData(state.scopeData, scopeData)
      : state.scopeData,
    initiativeScopeData: state.initiativeScopeData,
    permissions: state.permissions,
  });
};

export const removeFutureState = (
  state: InitiativePage$State,
  futureStateId: ArdoqId
): InitiativePage$State =>
  updateLoadedInitiative(
    state,
    initiative => removeFutureStatePreview(initiative, futureStateId),
    null
  );

export const applyUpdatedInitiative = (
  state: InitiativePage$State,
  result: Result<InitiativeUpdateResponse>
): InitiativePage$State => {
  if (state.isLoading || isArdoqError(result)) return state;
  const current = viewedInitiative(state, result.initiative._id);
  if (!current) return state;
  return rebuildInitiative(state, current, result);
};

export const viewedInitiative = (
  state: InitiativePage$State,
  initiativeId: ArdoqId
): Initiative | null =>
  !state.isLoading &&
  !isArdoqError(state.initiativeResult) &&
  state.initiativeResult._id === initiativeId
    ? state.initiativeResult
    : null;

export const applyWebsocketInitiativeUpdate = (
  state: InitiativePage$State,
  event: InitiativeUpdatedEvent
): InitiativePage$State => {
  if (state.isLoading) return state;
  const current = viewedInitiative(state, event.initiative._id);
  if (!current || event.initiative._version < current._version) return state;
  return rebuildInitiative(state, current, event);
};

export const applyWebsocketInitiativeDelete = (
  state: InitiativePage$State,
  initiativeId: ArdoqId
): InitiativePage$State => {
  if (state.isLoading) return state;
  return viewedInitiative(state, initiativeId)
    ? toLoadedState({
        initiativeResult: toArdoqError({
          error: new Error('Initiative deleted'),
        }),
        owners: state.owners,
        scopeData: state.scopeData,
        initiativeScopeData: state.initiativeScopeData,
        permissions: state.permissions,
      })
    : state;
};

export const mergeFutureState = (
  state: InitiativePage$State,
  { futureState, scopeData }: FutureStateWebsocketEvent
): InitiativePage$State =>
  updateLoadedInitiative(
    state,
    initiative => mergeFutureStatePreview(initiative, futureState),
    scopeData
  );

export const addFutureState = (
  state: InitiativePage$State,
  { futureState, scopeData }: FutureStateWebsocketEvent
): InitiativePage$State =>
  updateLoadedInitiative(
    state,
    initiative => addFutureStatePreview(initiative, futureState),
    scopeData
  );

const selectApproval = (
  state: InitiativePage$State
): FutureStateApprovalSummary | null =>
  state.isLoading ? null : toApprovalSummary(state.initiativeResult);

export const toInitiativeSummary = (
  initiative: Initiative
): InitiativeSummary => ({
  _id: initiative._id,
  name: initiative.name,
  description: initiative.description,
  futureStateIds: initiative.futureStates.map(futureState => futureState._id),
});

const selectInitiativeSummary = (
  state: InitiativePage$State
): InitiativeSummary | null =>
  state.isLoading || isArdoqError(state.initiativeResult)
    ? null
    : toInitiativeSummary(state.initiativeResult);

const initiativeComponentScopeData$ = activeInitiativeId$.pipe(
  switchMap(id =>
    from(traversalApi.loadTraversal(getWildcardTraversalPayload(id))).pipe(
      map(result =>
        isArdoqError(result)
          ? null
          : scopeDataOperations.enhance(result.scopeData)
      )
    )
  ),
  startWith(null)
);

export const initiativePage$ = reducedStream<InitiativePage$State>(
  'initiativePage$',
  defaultState,
  [
    streamReducer(currentInitiative$, setCurrentInitiative),
    streamReducer(deleteFutureState$, removeFutureState),
    streamReducer(updateInitiative$, applyUpdatedInitiative),
    streamReducer(updateStage$, applyUpdatedInitiative),
    streamReducer(
      initiativeComponentScopeData$,
      (state, initiativeScopeData) =>
        state.isLoading ? state : { ...state, initiativeScopeData }
    ),
    streamReducer(
      websocket$.pipe(
        filterResourceType<InitiativeUpdatedEvent>(ResourceType.INITIATIVE)
      ),
      websocketReducer({
        update: applyWebsocketInitiativeUpdate,
        delete: applyWebsocketInitiativeDelete,
      })
    ),
    streamReducer(
      websocket$.pipe(
        filterResourceType<FutureStateWebsocketEvent>(ResourceType.FUTURE_STATE)
      ),
      websocketReducer<InitiativePage$State, FutureStateWebsocketEvent>({
        create: addFutureState,
        update: mergeFutureState,
        delete: removeFutureState,
      })
    ),
  ]
);

export const currentInitiativeSummary$ = initiativePage$.pipe(
  map(selectInitiativeSummary),
  distinctUntilChanged(isEqual)
);

export const initiativeHasApprovedFutureState$ = initiativePage$.pipe(
  map(selectApproval),
  distinctUntilChanged(isEqual)
);

export const currentInitiativeId$ = initiativePage$.pipe(
  map(state =>
    !state.isLoading && !isArdoqError(state.initiativeResult)
      ? state.initiativeResult._id
      : null
  ),
  filter(isTruthy),
  distinctUntilChanged()
);

export type InitiativeRelatedStatuses = Pick<
  Initiative,
  'objectivesStatus' | 'capabilityDeltasStatus' | 'impactedApplicationsStatus'
>;

const ALL_RELATED_VISIBLE: InitiativeRelatedStatuses = {
  objectivesStatus: 'ok',
  capabilityDeltasStatus: 'ok',
  impactedApplicationsStatus: 'ok',
};

export const toInitiativeRelatedStatuses = (
  state: InitiativePage$State
): InitiativeRelatedStatuses => {
  if (state.isLoading || isArdoqError(state.initiativeResult)) {
    return ALL_RELATED_VISIBLE;
  }
  const {
    objectivesStatus,
    capabilityDeltasStatus,
    impactedApplicationsStatus,
  } = state.initiativeResult;
  return {
    objectivesStatus,
    capabilityDeltasStatus,
    impactedApplicationsStatus,
  };
};

export const currentInitiativeRelatedStatuses$ = initiativePage$.pipe(
  map(toInitiativeRelatedStatuses),
  distinctUntilChanged(isEqual)
);

export const currentInitiativePermissions$ = initiativePage$.pipe(
  map(state => (state.isLoading ? UNKNOWN_PERMISSIONS : state.permissions)),
  distinctUntilChanged(isEqual)
);

export const currentInitiativeScopeData$ = initiativePage$.pipe(
  map(state => (state.isLoading ? null : state.initiativeScopeData)),
  distinctUntilChanged()
);
